import React from 'react';
import DropdownMenu from '../dropdown-menu';
import SelectionCheckbox from '../selection-checkbox';
import searchIcon from '../../assets/icons/search-small.svg';
import chevronIcon from '../../assets/icons/chevron-small.svg';
import groupCaretIcon from '../../assets/icons/group-caret.svg';
import sortCaretsIcon from '../../assets/icons/sort-carets.svg';
import './data-grid.css';

/**
 * Briques du data-grid v4 partagées par les écrans en tableau (puits de
 * commandes, suivi des livraisons…). Les colonnes restent propres à chaque
 * page : seules la barre d'outils, la pagination et les en-têtes de groupe
 * sont communs.
 */

export const GridChevron = () => (
  <img
    src={chevronIcon}
    alt=""
    width="6.06218"
    height="3.375"
    className="agv-dg-chevron"
  />
);

export const GridSearch = ({ value, onChange, placeholder }) => (
  <label className="agv-dg-search">
    <img src={searchIcon} alt="" width="14" height="14" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
    />
  </label>
);

/**
 * Bouton « Filtres » avec compteur, ouvrant un popover libre
 */
export const GridFiltersButton = ({ activeCount, width = 320, children }) => (
  <DropdownMenu
    align="left"
    width={width}
    renderTrigger={({ toggle }) => (
      <button type="button" className="agv-dg-filters-btn" onClick={toggle}>
        <span className="agv-dg-filters-btn__glyph">⩩</span>
        Filtres
        {activeCount > 0 && (
          <span className="agv-dg-filters-btn__badge">{activeCount}</span>
        )}
        <GridChevron />
      </button>
    )}
  >
    {children}
  </DropdownMenu>
);

/**
 * Jeton d'un filtre actif : « Clé : valeur ✕ »
 */
export const FilterToken = ({ label, value, onRemove }) => (
  <span className="agv-dg-token">
    <span className="agv-dg-token__key">{label} :</span>
    <span className="agv-dg-token__value">{value}</span>
    <button
      type="button"
      className="agv-dg-token__remove"
      onClick={onRemove}
      title="Retirer ce filtre"
    >
      ✕
    </button>
  </span>
);

/**
 * Bouton « Grouper : … » ouvrant la liste des regroupements possibles
 */
export const GroupByButton = ({ options, value, onChange }) => (
  <DropdownMenu
    width={220}
    items={options.map((option) => ({
      label: option.label,
      hint: option.value === value ? '✓' : null,
      onClick: () => onChange(option.value),
    }))}
    renderTrigger={({ toggle }) => (
      <button type="button" className="agv-dg-ghost-btn" onClick={toggle}>
        <span className="agv-dg-ghost-btn__key">Grouper :</span>
        <span className="agv-dg-ghost-btn__value">
          {options.find((option) => option.value === value)?.label}
        </span>
        <GridChevron />
      </button>
    )}
  />
);

/**
 * Pagination compacte : « 1–24 sur 128 » + pages (première, voisines, dernière)
 */
export const GridPagination = ({ paginationInfo, onOffsetChange, unit }) => {
  const total = paginationInfo?.total_items || 0;
  if (total === 0) return null;

  const perPage = paginationInfo.items_per_page || 1;
  const current = paginationInfo.current_page || 1;
  const totalPages = paginationInfo.total_pages || 1;
  const start = (current - 1) * perPage + 1;
  const end = Math.min(current * perPage, total);

  const pages = [...new Set([1, current - 1, current, current + 1, totalPages])]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  const goTo = (page) => onOffsetChange((page - 1) * perPage);

  return (
    <div className="agv-dg-pagination">
      <span className="agv-dg-pagination__range">
        {start}–{end} sur {total}
        {unit && ` ${unit}`}
      </span>
      <div className="agv-dg-pagination__pages">
        <button
          type="button"
          className="agv-dg-page-btn"
          disabled={current <= 1}
          onClick={() => goTo(current - 1)}
          title="Page précédente"
        >
          ‹
        </button>
        {pages.map((page, index) => (
          <React.Fragment key={page}>
            {index > 0 && page - pages[index - 1] > 1 && (
              <span className="agv-dg-page-btn agv-dg-page-btn--gap">…</span>
            )}
            <button
              type="button"
              className={`agv-dg-page-btn ${
                page === current ? 'agv-dg-page-btn--active' : ''
              }`}
              onClick={() => goTo(page)}
            >
              {page}
            </button>
          </React.Fragment>
        ))}
        <button
          type="button"
          className="agv-dg-page-btn"
          disabled={current >= totalPages}
          onClick={() => goTo(current + 1)}
          title="Page suivante"
        >
          ›
        </button>
      </div>
    </div>
  );
};

/**
 * En-tête de groupe repliable : flèche, titre puis éléments secondaires
 * séparés par « · » (chaînes ou { label, className, color })
 */
export const GroupHeader = ({
  title,
  parts = [],
  totals,
  collapsed,
  onToggle,
  selection,
}) => (
  <div
    role="button"
    tabIndex={0}
    className="agv-dg-group-header"
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    }}
    aria-expanded={!collapsed}
  >
    {selection && (
      <SelectionCheckbox
        checked={selection.state === 'all'}
        indeterminate={selection.state === 'some'}
        onChange={selection.onChange}
        disabled={selection.disabled}
        title={selection.title}
      />
    )}
    <img
      src={groupCaretIcon}
      alt=""
      width="7.79423"
      height="4.5"
      className={`agv-dg-group-header__caret ${
        collapsed ? 'agv-dg-group-header__caret--collapsed' : ''
      }`}
    />
    <span className="agv-dg-group-header__name">{title}</span>
    {parts.filter(Boolean).map((part, index) => {
      const { label, className, color } =
        typeof part === 'string' ? { label: part } : part;
      return (
        <React.Fragment key={`${label}-${index}`}>
          {index > 0 && <span className="agv-dg-group-header__dot">·</span>}
          <span
            className={`agv-dg-group-header__meta ${className || ''}`}
            style={color ? { color } : undefined}
          >
            {label}
          </span>
        </React.Fragment>
      );
    })}
    {totals && <span className="agv-dg-group-header__totals">{totals}</span>}
  </div>
);

/**
 * Statut en point de couleur + libellé (pas de pastille)
 */
export const StatusDot = ({ color, label }) => (
  <span className="agv-dg-status">
    <span className="agv-dg-dot" style={{ backgroundColor: color }} />
    {label}
  </span>
);

/**
 * Cellule d'en-tête triable : flèche ↓/↑ quand la colonne porte le tri,
 * double chevron discret sinon. Un clic inverse le sens ou active la colonne.
 */
export const SortableHeaderCell = ({
  className,
  label,
  field,
  sortField,
  sortOrder,
  onSort,
}) => {
  const isActive = field === sortField;
  return (
    <button
      type="button"
      className={`agv-dg-cell agv-dg-head-sort ${className || ''}`}
      onClick={() =>
        onSort(field, isActive && sortOrder === 'DESC' ? 'ASC' : 'DESC')
      }
      title="Trier"
    >
      {label}
      {isActive ? (
        <span className="agv-dg-head-sort__arrow">
          {sortOrder === 'DESC' ? '↓' : '↑'}
        </span>
      ) : (
        <img src={sortCaretsIcon} alt="" width="6" height="8.5" />
      )}
    </button>
  );
};
